import {
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";

//Components
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import PaginatedTableWrapper from "../PaginatedTableWrapper";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import SearchModal from "../SearchModal";
import SearchButtons from "../SearchButtons";

//Fetching
// import { useLazyQuery } from '@apollo/client';
// import { TICKET_SEARCH_QUERY } from '../../Queries/TicketSearchQuery';

//Contexts
import { UserContext } from "../../Contexts/UserContext";

//styling
import { withStyles } from "@material-ui/core/styles";
import { useStyles } from "../../Styles/styles";

//utils
import { formatOfficerName } from "../../utils/utils";
import {
  parseTicketSearch,
  ticketSearchHeaders,
} from "../../utils/SearchHelpers";
import HelpIconButton from "../HelpIconButton";
import OriAutocomplete from "../OriAutocomplete/OriAutocomplete";
import { useFetchOfficers } from "../../CustomHooks/useFetchOfficers";
import { useTicketSearch } from "../../CustomHooks/useTicketSearch";
import PropTypes from "prop-types";

import moment from "moment";

const initialSearchState = oriId => {
  return {
    ori_id: oriId || null,
    ticket_number: "",
    ticket_violation_prototype_attributes: {
      name: "",
      entry_name: "",
    },
    officer_id: null,
    drivers_license: "",
    state_txt: "",
    created_at_search_start_date: moment()
      .local()
      .subtract(1, "years")
      .format("MM/DD/YYYY"),
    created_at_search_end_date: moment().format("MM/DD/YYYY"),
  };
};

const StyledAccordionSummary = withStyles({
  root: {
    backgroundColor: props =>
      props.nightmode === "true" ? "#616161" : "#fae8ad",
    borderBottom: "1px solid rgba(0, 0, 0, .125)",
    marginBottom: -1,
    minHeight: 56,
    "&$expanded": {
      minHeight: 56,
    },
  },
  content: {
    "&$expanded": {
      margin: "0",
    },
  },
  expanded: {},
})(AccordionSummary);

const TicketSearch = ({
  showModal,
  setShowModal,
  handleIncidentOpening,
  handleTicketOpening,
  handlePersonOpening,
}) => {
  const classes = useStyles();
  const { user, responseErrorHandler, nightMode, onlineState } =
    useContext(UserContext);
  const inputLabel = useRef(null);
  const dashRef = useRef(null);
  const [showSearchOptions, setShowSearchOptions] = useState(true);
  const [searchState, setSearchState] = useState(
    initialSearchState(user?.homeOriId?.toString())
  );
  const [dashSize, setDashSize] = useState({
    width: dashRef?.current?.offsetWidth,
    height: undefined,
  });
  const { officers, officersLoading } = useFetchOfficers(
    searchState?.ori_id,
    onlineState
  );
  const memoizedOfficers = useMemo(() => officers, [officers]);

  const {
    searchResults,
    setSearchResults,
    submitSearch,
    resultsPerPage,
    setResultsPerPage,
    ticketSearchLoading,
    ticketSearchError,
  } = useTicketSearch(user, responseErrorHandler, setShowSearchOptions);

  const parsedSearchResults = useMemo(() => {
    return searchResults
      ? parseTicketSearch(
          searchResults,
          handleIncidentOpening,
          handleTicketOpening,
          setShowModal,
          handlePersonOpening
        )
      : [];
  }, [
    searchResults,
    handleIncidentOpening,
    handleTicketOpening,
    setShowModal,
    handlePersonOpening,
  ]);

  const handleTicketChange = useCallback((value, name) => {
    setSearchState(prevState => ({
      ...prevState,
      ticket_violation_prototype_attributes: {
        ...prevState.ticket_violation_prototype_attributes,
        [name]: value,
      },
    }));
  }, []);

  const handleChange = useCallback((value, name) => {
    setSearchState(prevState => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const handlePaginatedSearch = (perPage, page) => {
    submitSearch(searchState, perPage, page);
  };

  useEffect(() => {
    if (searchState.ori_id && searchState.ori_id !== "") {
      handleChange(null, "officer_id");
    }
  }, [handleChange, searchState.ori_id]);

  useEffect(() => {
    const handleResize = () => {
      if (dashRef.current) {
        setDashSize({
          width: dashRef.current.offsetWidth,
          height: dashRef.current.offsetHeight,
        });
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Set the initial size

    return () => window.removeEventListener("resize", handleResize);
  }, [dashRef, showModal]);

  return showModal ? (
    <SearchModal showModal={showModal} setShowModal={setShowModal}>
      <form
        className={classes.paper}
        ref={dashRef}
        style={{ display: "flex", flexDirection: "column", padding: 0 }}
      >
        <Accordion
          square
          expanded={showSearchOptions}
          onChange={() => setShowSearchOptions(!showSearchOptions)}
          TransitionProps={{ unmountOnExit: false }}
        >
          <StyledAccordionSummary
            nightmode={nightMode?.toString()}
            style={{ margin: 0 }}
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Box flexGrow="1" display="flex" justifyContent="space-between">
              <Typography
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: nightMode ? "white" : "#303e4f",
                }}
                className={classes.AccordionTypography}
                data-cy="initial-call-information-panel-header"
              >
                Ticket Search{" "}
                {showSearchOptions ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </Typography>
              <HelpIconButton objectname="mobile_ticket_search" />
            </Box>
          </StyledAccordionSummary>
          <AccordionDetails type="button" onClick={e => e.stopPropagation()}>
            <div
              style={{ display: "flex", flexDirection: "column", padding: 0 }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  flexWrap: "wrap",
                  height: "auto",
                  justifyContent: "flex-start",
                }}
              >
                <OriAutocomplete
                  onlineState={onlineState}
                  selectedOri={searchState?.ori_id}
                  handleChange={(e, value) => handleChange(value?.id, "ori_id")}
                />
                <TextField
                  data-testid="ticket-number"
                  name={"ticket_number"}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="ticket-number-id"
                  label="Ticket Number"
                  value={searchState?.ticket_number}
                  className={classes.matchMargin}
                  onChange={e => handleChange(e.target.value, "ticket_number")}
                />
                <TextField
                  data-testid="ticket-violation"
                  name={"name"}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="ticket-violation-id"
                  label="Violation"
                  value={
                    searchState?.ticket_violation_prototype_attributes?.name
                  }
                  className={classes.matchMargin}
                  onChange={e => handleTicketChange(e.target.value, "name")}
                />
                <TextField
                  data-testid="ticket-type"
                  name={"entry_name"}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="ticket-type-id"
                  label="Ticket Type"
                  value={
                    searchState?.ticket_violation_prototype_attributes
                      ?.entry_name
                  }
                  className={classes.matchMargin}
                  onChange={e =>
                    handleTicketChange(e.target.value, "entry_name")
                  }
                />
                <Autocomplete
                  loading={officersLoading}
                  autoSelect
                  multiple={false}
                  value={
                    memoizedOfficers?.find(
                      officer => officer?.id === searchState?.officer_id
                    ) || null
                  }
                  id="ticket-search-officer-autocomplete"
                  options={memoizedOfficers}
                  getOptionLabel={option => formatOfficerName(option)}
                  style={{ width: 300 }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        style: { borderRadius: "5px" },
                        ...params.InputProps,
                      }}
                      label="Issued By"
                    />
                  )}
                  onChange={(e, value) => handleChange(value?.id, "officer_id")}
                  autoHighlight
                  className={classes.matchMargin}
                  data-testid="officer-autocomplete"
                />
                <TextField
                  data-testid="drivers-license"
                  name={"drivers_license"}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="drivers-license-id"
                  label="License Number"
                  value={searchState?.drivers_license}
                  className={classes.matchMargin}
                  onChange={e =>
                    handleChange(e.target.value, "drivers_license")
                  }
                />
                <TextField
                  data-testid="state-txt"
                  name={"state_txt"}
                  InputProps={{ style: { borderRadius: "5px" } }}
                  InputLabelProps={{ shrink: true }}
                  id="state-txt-id"
                  label="License State"
                  value={searchState?.state_txt}
                  className={classes.matchMargin}
                  onChange={e => handleChange(e.target.value, "state_txt")}
                />
                <FormControl variant="standard" className={classes.formControl}>
                  <KeyboardDatePicker
                    data-testid="start-date"
                    clearable
                    showTodayButton
                    disableFuture
                    maxDateMessage={"Date cannot be in the future"}
                    label="Ticket Date Start"
                    value={
                      searchState.created_at_search_start_date
                        ? moment.utc(
                            searchState.created_at_search_start_date,
                            "MM-DD-YYYY"
                          )
                        : null
                    }
                    onChange={dateObj =>
                      handleChange(
                        dateObj?.format("MM/DD/YYYY") || "",
                        "created_at_search_start_date"
                      )
                    }
                    format="MM/DD/YYYY"
                    inputProps={{ "data-testid": "start-date-input" }}
                    InputProps={{ style: { borderRadius: "5px" } }}
                    InputLabelProps={{ shrink: true }}
                    style={{ width: "100%" }}
                  />
                </FormControl>
                <FormControl variant="standard" className={classes.formControl}>
                  <KeyboardDatePicker
                    data-testid="end-date"
                    clearable
                    showTodayButton
                    disableFuture
                    maxDateMessage={"Date cannot be in the future"}
                    label="Ticket Date End"
                    value={
                      searchState.created_at_search_end_date
                        ? moment.utc(
                            searchState.created_at_search_end_date,
                            "MM-DD-YYYY"
                          )
                        : null
                    }
                    onChange={dateObj =>
                      handleChange(
                        dateObj?.format("MM/DD/YYYY") || "",
                        "created_at_search_end_date"
                      )
                    }
                    format="MM/DD/YYYY"
                    inputProps={{ "data-testid": "end-date-input" }}
                    InputProps={{ style: { borderRadius: "5px" } }}
                    InputLabelProps={{ shrink: true }}
                    style={{ width: "100%" }}
                  />
                </FormControl>
                <div className={classes.matchMargin}>
                  <InputLabel
                    shrink={true}
                    className={classes.label}
                    data-testid={"results-per-page-label"}
                    ref={inputLabel}
                    id={"results-per-page-label"}
                  >
                    Results Per Page
                  </InputLabel>
                  <Select
                    data-testid={"results-per-page-select"}
                    inputProps={{
                      "data-testid": "results-per-page-select-button",
                    }}
                    id={"results-per-page-select"}
                    name={"resultPerPage"}
                    value={resultsPerPage}
                    native={false}
                    onChange={e => setResultsPerPage(e.target.value)}
                    style={{ marginTop: "0px", borderRadius: "5px" }}
                  >
                    <MenuItem
                      data-testid={"10-results-per-page"}
                      key={10}
                      value={10}
                    >
                      10
                    </MenuItem>
                    <MenuItem
                      data-testid={"25-results-per-page"}
                      key={25}
                      value={25}
                    >
                      25
                    </MenuItem>
                    <MenuItem
                      data-testid={"50-results-per-page"}
                      key={50}
                      value={50}
                    >
                      50
                    </MenuItem>
                  </Select>
                </div>
              </div>
              <SearchButtons
                id={"ticket-search-buttons"}
                loading={ticketSearchLoading}
                submitSearch={handlePaginatedSearch}
                setSearchResults={setSearchResults}
                setShowModal={setShowModal}
                resetSearchState={() => setSearchState(initialSearchState(""))}
                clearSearchResults={() => setSearchResults(null)}
              />
            </div>
          </AccordionDetails>
        </Accordion>
        {!!searchResults && (
          <PaginatedTableWrapper
            dashSize={dashSize}
            tableHeight={dashRef?.current?.offsetHeight * 0.95}
            tableName={"Ticket Search Results"}
            headers={ticketSearchHeaders}
            userId={user?.id}
            paginatedQueryHandler={handlePaginatedSearch}
            rows={parsedSearchResults}
            loading={ticketSearchLoading}
            error={ticketSearchError}
            paginated={true}
            rowsPerPage={resultsPerPage}
          />
        )}
      </form>
    </SearchModal>
  ) : null;
};

TicketSearch.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
  handleIncidentOpening: PropTypes.func.isRequired,
  handleTicketOpening: PropTypes.func.isRequired,
  handlePersonOpening: PropTypes.func.isRequired,
};

export default TicketSearch;
